import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/api';
import {
  Building2, FileSpreadsheet, Plus, Pencil, Trash2, Users, Loader,
  X, AlertCircle, ChevronRight, Columns, Save, FileUp
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AVAILABLE_FIELDS = [
  { value: 'row_number', label: 'Auto-increment (№)' },
  { value: 'hs_code', label: 'HS Code' },
  { value: 'commodity', label: 'Commodity/Description' },
  { value: 'gross_weight', label: 'Gross Weight (kg)' },
  { value: 'net_weight', label: 'Net Weight (kg)' },
  { value: 'invoice_ammount', label: 'Invoice Amount' },
  { value: 'invoice_currency', label: 'Currency' },
  { value: 'country_of_origin', label: 'Country of Origin' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'unit_of_measure', label: 'Unit of Measure' },
  { value: 'unit_of_measure_code', label: 'Unit of Measure Code' },
  { value: 'package_count', label: 'Package Count' },
  { value: 'package_type', label: 'Package Type' },
  { value: 'product_code', label: 'Product Code / SKU' },
  { value: 'price_per_unit', label: 'Price per Unit (calculated)' },
];

const isUserfileField = (field) => field && field.startsWith('userfile_');

// ─── Companies Section ──────────────────────────────────────────────────────

const CompaniesTable = ({ companies, templates, onEdit, onDelete, onRefresh }) => {
  if (!companies.length) {
    return (
      <div className="text-center py-12 text-neutral-400">
        <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No companies yet. Create one to get started.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-neutral-50 border-b border-neutral-200">
          <tr>
            <th className="text-left px-6 py-3 text-sm font-semibold text-neutral-700">Company</th>
            <th className="text-left px-6 py-3 text-sm font-semibold text-neutral-700">Default Template</th>
            <th className="text-center px-6 py-3 text-sm font-semibold text-neutral-700">Users</th>
            <th className="text-right px-6 py-3 text-sm font-semibold text-neutral-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.map(company => (
            <tr key={company.id} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-neutral-400" />
                  <span className="font-medium text-neutral-900">{company.name}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                {company.default_template_name ? (
                  <span className="text-sm text-neutral-600">
                    {company.default_template_name}
                  </span>
                ) : (
                  <span className="text-sm text-neutral-400">Not set</span>
                )}
              </td>
              <td className="px-6 py-4 text-center">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                  <Users className="h-3.5 w-3.5" />
                  {company.user_count}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onEdit(company)}
                    className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-600 hover:text-neutral-900"
                    title="Edit company"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(company)}
                    className="p-2 hover:bg-red-50 rounded-lg text-neutral-600 hover:text-red-600"
                    title="Delete company"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CompanyModal = ({ company, templates, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: company?.name || '',
    default_template_id: company?.default_template_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addUserId, setAddUserId] = useState('');

  useEffect(() => {
    if (company?.id) {
      setLoadingUsers(true);
      adminApi.getUsers()
        .then(r => setAllUsers(r.data))
        .catch(() => {})
        .finally(() => setLoadingUsers(false));
    }
  }, [company?.id]);

  const members = allUsers.filter(u => u.company_id === company?.id);
  const availableUsers = allUsers.filter(u => !u.company_id || u.company_id !== company?.id);

  const addMember = async () => {
    if (!addUserId) return;
    try {
      await adminApi.updateUser(parseInt(addUserId), { company_id: company.id });
      setAllUsers(prev => prev.map(u => u.id === parseInt(addUserId) ? { ...u, company_id: company.id } : u));
      setAddUserId('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add user');
    }
  };

  const removeMember = async (userId) => {
    try {
      await adminApi.updateUser(userId, { clear_company: true });
      setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, company_id: null } : u));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError('Company name is required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        default_template_id: form.default_template_id || null,
        clear_default_template: !form.default_template_id,
      };

      if (company) {
        await adminApi.updateCompany(company.id, payload);
      } else {
        await adminApi.createCompany({ name: form.name.trim() });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <h2 className="font-bold text-neutral-900">
            {company ? `Edit ${company.name}` : 'Create Company'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Company Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Smartway"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {company && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Default Template</label>
              <select
                value={form.default_template_id}
                onChange={e => setForm(f => ({ ...f, default_template_id: e.target.value }))}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="">No default template</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.template_name}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-1">Users in this company will use this template by default</p>
            </div>
          )}

          {/* Members Section */}
          {company?.id && (
            <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
              <h3 className="font-semibold text-neutral-900 text-sm mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-neutral-500" />
                Members ({members.length})
              </h3>

              {loadingUsers ? (
                <div className="flex items-center gap-2 py-3 text-neutral-400 text-sm">
                  <Loader className="h-4 w-4 animate-spin" /> Loading...
                </div>
              ) : (
                <>
                  {/* Add member */}
                  <div className="flex gap-2 mb-3">
                    <select
                      value={addUserId}
                      onChange={e => setAddUserId(e.target.value)}
                      className="flex-1 border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      <option value="">Add user...</option>
                      {availableUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                      ))}
                    </select>
                    <button
                      onClick={addMember}
                      disabled={!addUserId}
                      className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Member list */}
                  {members.length === 0 ? (
                    <p className="text-sm text-neutral-400 text-center py-2">No members yet</p>
                  ) : (
                    <div className="space-y-1">
                      {members.map(m => (
                        <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-neutral-200">
                          <div>
                            <span className="text-sm font-medium text-neutral-800">{m.username}</span>
                            <span className="text-xs text-neutral-400 ml-2">{m.email}</span>
                          </div>
                          <button
                            onClick={() => removeMember(m.id)}
                            className="p-1 hover:bg-red-50 rounded text-neutral-400 hover:text-red-600"
                            title="Remove from company"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            {company ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Templates Section ──────────────────────────────────────────────────────

const TemplatesTable = ({ templates, onEdit, onDelete, onView }) => {
  if (!templates.length) {
    return (
      <div className="text-center py-12 text-neutral-400">
        <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No templates yet. Create one to customize Excel output.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-neutral-50 border-b border-neutral-200">
          <tr>
            <th className="text-left px-6 py-3 text-sm font-semibold text-neutral-700">Template</th>
            <th className="text-left px-6 py-3 text-sm font-semibold text-neutral-700">Company</th>
            <th className="text-center px-6 py-3 text-sm font-semibold text-neutral-700">Columns</th>
            <th className="text-center px-6 py-3 text-sm font-semibold text-neutral-700">Users</th>
            <th className="text-right px-6 py-3 text-sm font-semibold text-neutral-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map(template => (
            <tr key={template.id} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-neutral-400" />
                  <div>
                    <div className="font-medium text-neutral-900">{template.template_name}</div>
                    {template.is_default && (
                      <span className="text-xs text-primary-600 font-medium">Default</span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-sm text-neutral-600">{template.company_name}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium">
                  <Columns className="h-3.5 w-3.5" />
                  {template.column_count}
                </span>
              </td>
              <td className="px-6 py-4 text-center">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                  <Users className="h-3.5 w-3.5" />
                  {template.total_users}
                </span>
                {template.direct_users > 0 && (
                  <span className="block text-xs text-neutral-400 mt-1">
                    {template.direct_users} direct
                  </span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onView(template)}
                    className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-600 hover:text-neutral-900"
                    title="View template"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-600 hover:text-neutral-900"
                    title="Edit template"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(template)}
                    className="p-2 hover:bg-red-50 rounded-lg text-neutral-600 hover:text-red-600"
                    title="Delete template"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TemplateModal = ({ template, onClose, onSaved }) => {
  const [form, setForm] = useState({
    company_name: template?.company_name || '',
    template_name: template?.template_name || '',
    is_default: template?.is_default || false,
    columns: template?.columns?.columns || [],
    userfile_config: template?.columns?.userfile_config || {
      enabled: false,
      join_field: '',
      join_userfile_column: '',
    },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addColumn = () => {
    setForm(f => ({
      ...f,
      columns: [...f.columns, {
        index: f.columns.length,
        header: '',
        field: null,
        transform: null
      }]
    }));
  };

  const updateColumn = (index, updates) => {
    const updated = [...form.columns];
    updated[index] = { ...updated[index], ...updates };
    setForm(f => ({ ...f, columns: updated }));
  };

  const removeColumn = (index) => {
    const updated = form.columns.filter((_, i) => i !== index);
    // Re-index
    updated.forEach((col, i) => col.index = i);
    setForm(f => ({ ...f, columns: updated }));
  };

  const moveColumn = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === form.columns.length - 1) return;

    const updated = [...form.columns];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];

    // Re-index
    updated.forEach((col, i) => col.index = i);
    setForm(f => ({ ...f, columns: updated }));
  };

  const save = async () => {
    if (!form.company_name.trim()) {
      setError('Company name is required');
      return;
    }
    if (!form.template_name.trim()) {
      setError('Template name is required');
      return;
    }
    if (form.columns.length === 0) {
      setError('Add at least one column');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const columnsData = { columns: form.columns };
      if (form.userfile_config?.enabled) {
        columnsData.userfile_config = form.userfile_config;
      }

      const payload = {
        company_name: form.company_name.trim(),
        template_name: form.template_name.trim(),
        is_default: form.is_default,
        columns: columnsData
      };

      if (template) {
        await adminApi.updateTemplate(template.id, {
          template_name: form.template_name.trim(),
          is_default: form.is_default,
          columns: columnsData
        });
      } else {
        await adminApi.createTemplate(payload);
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <h2 className="font-bold text-neutral-900">
            {template ? `Edit ${template.template_name}` : 'Create Template'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
          {/* Template Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Company Name</label>
              <input
                type="text"
                value={form.company_name}
                onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                placeholder="e.g., Smartway"
                disabled={!!template}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-neutral-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">Template Name</label>
              <input
                type="text"
                value={form.template_name}
                onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))}
                placeholder="e.g., Smartway Standard"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
              className="w-4 h-4 rounded accent-primary-600"
            />
            <span className="text-sm font-medium text-neutral-700">Mark as default template</span>
          </label>

          {/* Columns Editor */}
          <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-neutral-900">Columns Configuration ({form.columns.length})</h3>
              <button
                onClick={addColumn}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add Column
              </button>
            </div>

            {form.columns.length === 0 ? (
              <div className="border-2 border-dashed border-neutral-200 rounded-lg p-8 text-center text-neutral-400 bg-white">
                <Columns className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No columns yet. Click "Add Column" to start.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {form.columns.map((col, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 border border-neutral-200 rounded-lg bg-white">
                    <span className="text-sm font-mono font-semibold text-neutral-500 w-8">
                      {String.fromCharCode(65 + i)}
                    </span>
                    <input
                      type="text"
                      placeholder="Column header (e.g., Код товару)"
                      value={col.header}
                      onChange={e => updateColumn(i, { header: e.target.value })}
                      className="flex-1 border border-neutral-200 rounded px-2 py-1.5 text-sm"
                    />
                    <select
                      value={isUserfileField(col.field) ? '_userfile' : (col.field || '')}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '_userfile') {
                          updateColumn(i, { field: 'userfile_', transform: null });
                        } else {
                          updateColumn(i, {
                            field: value || null,
                            transform: value === 'row_number' ? 'auto_increment' : null
                          });
                        }
                      }}
                      className="w-52 border border-neutral-200 rounded px-2 py-1.5 text-sm"
                    >
                      <option value="">Empty column</option>
                      {AVAILABLE_FIELDS.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                      <option value="_userfile">User File Column</option>
                    </select>
                    {isUserfileField(col.field) && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-neutral-500 whitespace-nowrap">Col:</span>
                        <input
                          type="text"
                          maxLength={1}
                          value={col.field?.replace('userfile_', '') || ''}
                          onChange={e => updateColumn(i, { field: `userfile_${e.target.value.toUpperCase()}` })}
                          placeholder="A"
                          className="w-10 border border-primary-300 rounded px-2 py-1.5 text-sm text-center font-mono bg-primary-50"
                        />
                      </div>
                    )}
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveColumn(i, 'up')}
                        disabled={i === 0}
                        className="p-1 hover:bg-neutral-200 rounded disabled:opacity-30"
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => moveColumn(i, 'down')}
                        disabled={i === form.columns.length - 1}
                        className="p-1 hover:bg-neutral-200 rounded disabled:opacity-30"
                        title="Move down"
                      >
                        ▼
                      </button>
                      <button
                        onClick={() => removeColumn(i)}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                        title="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* User File Settings */}
          <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.userfile_config?.enabled || false}
                onChange={e => setForm(f => ({
                  ...f,
                  userfile_config: { ...f.userfile_config, enabled: e.target.checked }
                }))}
                className="w-4 h-4 rounded accent-primary-600"
              />
              <FileUp className="h-4 w-4 text-neutral-500" />
              <span className="text-sm font-semibold text-neutral-900">User uploads own Excel file</span>
            </label>

            {form.userfile_config?.enabled && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Join field (extracted data)
                  </label>
                  <select
                    value={form.userfile_config?.join_field || ''}
                    onChange={e => setForm(f => ({
                      ...f,
                      userfile_config: { ...f.userfile_config, join_field: e.target.value }
                    }))}
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">Select field...</option>
                    {AVAILABLE_FIELDS.filter(f => f.value !== 'row_number' && f.value !== 'price_per_unit').map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">Which extracted field to match rows on</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Matching column in user's file
                  </label>
                  <input
                    type="text"
                    maxLength={1}
                    value={form.userfile_config?.join_userfile_column || ''}
                    onChange={e => setForm(f => ({
                      ...f,
                      userfile_config: { ...f.userfile_config, join_userfile_column: e.target.value.toUpperCase() }
                    }))}
                    placeholder="e.g., C"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Column letter (A-Z) in user's Excel for the join key</p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            <Save className="h-4 w-4" />
            {template ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Delete Confirmation Modal ──────────────────────────────────────────────

const DeleteModal = ({ item, type, onClose, onDeleted }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const itemName = type === 'company' ? item.name : item.template_name;

  const doDelete = async () => {
    setDeleting(true);
    try {
      if (type === 'company') {
        await adminApi.deleteCompany(item.id);
      } else {
        await adminApi.deleteTemplate(item.id);
      }
      onDeleted();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-neutral-100">
          <h2 className="font-bold text-neutral-900">Delete {type === 'company' ? 'Company' : 'Template'}</h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-neutral-600">
            Are you sure you want to delete <strong>{itemName}</strong>?
            {type === 'company' && ' Users will be unlinked but not deleted.'}
            {type === 'template' && ' Users and companies using this template will be unlinked.'}
          </p>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Type <span className="font-mono bg-neutral-100 px-1 rounded">{itemName}</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100">
          <button
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={doDelete}
            disabled={deleting || confirmText !== itemName}
            className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {deleting && <Loader className="h-4 w-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

export const TemplatesAdmin = () => {
  const [subtab, setSubtab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [companyModal, setCompanyModal] = useState(null);
  const [templateModal, setTemplateModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companiesRes, templatesRes] = await Promise.all([
        adminApi.getCompanies(),
        adminApi.getTemplates()
      ]);
      setCompanies(companiesRes.data);
      setTemplates(templatesRes.data);
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaved = () => {
    setCompanyModal(null);
    setTemplateModal(null);
    loadData();
  };

  const handleDeleted = () => {
    setDeleteModal(null);
    loadData();
  };

  const handleEditTemplate = async (template) => {
    try {
      // Fetch full template details with columns
      const response = await adminApi.getTemplateDetail(template.id);
      setTemplateModal(response.data);
    } catch (e) {
      console.error('Failed to load template details:', e);
      // Fallback to template from list (won't have columns)
      setTemplateModal(template);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setSubtab('companies')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            subtab === 'companies'
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          <Building2 className="h-4 w-4" />
          Companies
          <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">{companies.length}</span>
        </button>
        <button
          onClick={() => setSubtab('templates')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
            subtab === 'templates'
              ? 'bg-primary-600 text-white'
              : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
          }`}
        >
          <FileSpreadsheet className="h-4 w-4" />
          Templates
          <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">{templates.length}</span>
        </button>
      </div>

      {/* Create button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => subtab === 'companies' ? setCompanyModal({}) : setTemplateModal({})}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create {subtab === 'companies' ? 'Company' : 'Template'}
        </button>
      </div>

      {/* Tables */}
      {subtab === 'companies' ? (
        <CompaniesTable
          companies={companies}
          templates={templates}
          onEdit={company => setCompanyModal(company)}
          onDelete={company => setDeleteModal({ item: company, type: 'company' })}
          onRefresh={loadData}
        />
      ) : (
        <TemplatesTable
          templates={templates}
          onEdit={handleEditTemplate}
          onDelete={template => setDeleteModal({ item: template, type: 'template' })}
          onView={handleEditTemplate}
        />
      )}

      {/* Modals */}
      {companyModal && (
        <CompanyModal
          company={companyModal.id ? companyModal : null}
          templates={templates}
          onClose={() => setCompanyModal(null)}
          onSaved={handleSaved}
        />
      )}

      {templateModal && (
        <TemplateModal
          template={templateModal.id ? templateModal : null}
          onClose={() => setTemplateModal(null)}
          onSaved={handleSaved}
        />
      )}

      {deleteModal && (
        <DeleteModal
          item={deleteModal.item}
          type={deleteModal.type}
          onClose={() => setDeleteModal(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};
